import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { getBoardList, getListsByBoardId, Board, CardList } from '../../api';

// --- 스타일 ---
const ModalContainer = styled.div`
  padding: 1.5rem;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: var(--text-bright);
  font-size: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-normal);
  font-size: 0.9rem;
`;

const Select = styled.select`
  background-color: var(--bg-input);
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  padding: 0.5rem;
  border-radius: 4px;
  outline: none;
  font-size: 1rem;

  &:focus {
    border-color: var(--primary-accent);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

interface CreateCardSelectModalProps {
    onClose: () => void;
    onCreate: (boardId: string, listId: string, title: string) => void;
}

const CreateCardSelectModal: React.FC<CreateCardSelectModalProps> = ({ onClose, onCreate }) => {
    const [boards, setBoards] = useState<Board[]>([]);
    const [lists, setLists] = useState<CardList[]>([]);

    const [selectedBoardId, setSelectedBoardId] = useState('');
    const [selectedListId, setSelectedListId] = useState('');
    const [cardTitle, setCardTitle] = useState('');

    // 초기 로드 시 보드 목록 가져오기
    useEffect(() => {
        const fetchBoards = async () => {
            try {
                const response = await getBoardList();
                const boardData = response.data._embedded?.getList || [];
                setBoards(boardData);
                if (boardData.length > 0) {
                    setSelectedBoardId(boardData[0].id);
                }
            } catch (e) {
                console.error("Failed to load boards", e);
            }
        };
        fetchBoards();
    }, []);

    // 보드 선택 시 해당 보드의 리스트 가져오기
    useEffect(() => {
        if (!selectedBoardId) {
            setLists([]);
            setSelectedListId('');
            return;
        }

        const fetchLists = async () => {
            try {
                const response = await getListsByBoardId(selectedBoardId);
                const listData = response.data._embedded?.getList || [];
                setLists(listData);
                if (listData.length > 0) {
                    setSelectedListId(listData[0].id);
                } else {
                    setSelectedListId('');
                }
            } catch (e) {
                console.error("Failed to load lists", e);
            }
        };
        fetchLists();
    }, [selectedBoardId]);

    const handleCreate = () => {
        if (!selectedBoardId || !selectedListId || !cardTitle.trim()) {
            alert("보드, 리스트, 제목을 모두 입력해주세요.");
            return;
        }
        onCreate(selectedBoardId, selectedListId, cardTitle);
    };

    return (
        <Modal visible={true} onClose={onClose}>
            <ModalContainer>
                <Title>새 카드 생성</Title>

                <FormGroup>
                    <Label>보드 선택</Label>
                    <Select
                        value={selectedBoardId}
                        onChange={(e) => setSelectedBoardId(e.target.value)}
                    >
                        {boards.map(board => (
                            <option key={board.id} value={board.id}>{board.name}</option>
                        ))}
                    </Select>
                </FormGroup>

                <FormGroup>
                    <Label>리스트 선택</Label>
                    <Select
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}
                        disabled={!selectedBoardId}
                    >
                        {lists.length === 0 ? <option value="">리스트 없음</option> :
                            lists.map(list => (
                                <option key={list.id} value={list.id}>{list.name}</option>
                            ))
                        }
                    </Select>
                </FormGroup>

                <FormGroup>
                    <Label>카드 제목</Label>
                    <Input
                        placeholder="할 일을 입력하세요..."
                        value={cardTitle}
                        onChange={(e) => setCardTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                        autoFocus
                    />
                </FormGroup>

                <ButtonGroup>
                    <Button onClick={onClose} style={{ backgroundColor: 'transparent', border: '1px solid var(--border-color)' }}>취소</Button>
                    <Button onClick={handleCreate}>생성</Button>
                </ButtonGroup>
            </ModalContainer>
        </Modal>
    );
};

export default CreateCardSelectModal;